/******************************************************************************\
 * :$
 *
 * Copyright(c) 2022 SAS Institute Inc., Cary, NC, USA. All Rights Reserved.
 *
 * Name: SubmissionPillar1
 *
 * Purpose:
 *
 * Author: craig (Craig.Simpson@sas.com)
 *
 * Support: SAS(r) Solutions OnDemand
 *
 * Input:
 *
 * Output:
 *
 * Parameters: (if applicable)
 *
 * Dependencies/Assumptions:
 *
 * Usage:
 *
 * History:
 * ddmmmyyyy userid description (Change Code)
 * 29Sep2022 craig File created.
 \******************************************************************************/
import React, {Fragment, ReactNode, useEffect, useState} from "react";
import {
    IGoldStandardPillar,
    IPillarData, IResponse,
    IReviewerComment,
    Pillar
} from "../../../models/application/IApplication";
import {Button, Col, Container, Form, Row} from "react-bootstrap";
import parse from "html-react-parser";
import {getDataPost} from "../../../models/data/Data";
import {IOutcome} from "../../../models/request/Outcome";
import {GSContext} from "../../../context/context";
import {useNavigate} from "react-router-dom";
import {ConfirmDialog} from "../../../components/dialogs/ConfirmDialog";
import {GSEditor} from "../../../components/editor/GSEditor";
import {InfoDialog} from "../../../components/dialogs/InfoDialog";

interface PillarProps1 {
    disabled: boolean,
    pillarData: IGoldStandardPillar;
}

export const SubmissionPillar: React.FC<PillarProps1> = ({pillarData, disabled}: PillarProps1) => {

    const [sessionUser] = React.useContext(GSContext);
    const [concernsEditorLoaded, setConcernsEditorLoaded] = useState(false);
    const [accoladesEditorLoaded, setAccoladesEditorLoaded] = useState(false);
    const [showThankYou, setShowThankYou] = useState<boolean>(false);
    const [showSaved, setShowSaved] = useState<boolean>(false);
    const [showConfirm, setShowConfirm] = useState<boolean>(false);
    const [reviewerConcerns, setReviewerConcerns] = useState<string>(Array.from(pillarData?.Pillar?.Comments ?? [])[0].Concerns);
    const [reviewerAccolades, setReviewerAccolades] = useState<string>(Array.from(pillarData?.Pillar?.Comments ?? [])[0].Accolades);

    const navigate = useNavigate();

    useEffect(() => {
        setConcernsEditorLoaded(true);
        setAccoladesEditorLoaded(true);
    }, []);

    const toggleThankYou = () => {
        setShowThankYou(!showThankYou);
    }

    const toggleSaved = () => {
        setShowSaved(!showSaved);
    }

    const toggleConfirm = () => {
        setShowConfirm(!showConfirm);
    }

    const handleShow = (event: React.FormEvent<HTMLButtonElement>) => {
        setShowConfirm(true);
    }

    /**
     * handle the save and stay on page
     *
     * @param event
     */
    const handleSave = (event: React.FormEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        saveComments(event, true, false);
    }

    const handleSubmissionSubmit = (event: any) => {
        setShowConfirm(false);
        event.preventDefault();
        event.stopPropagation();

        saveComments(event, false, true);
    }


    /**
     * handle form submit
     *
     * @param event
     */
    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        saveComments(event, false, false);
    }

    const saveComments = (event: any, saveOnly: boolean, submitSub: boolean) => {
        const userID = sessionUser.UserID;
        //const submissionID = submissionData?.Submission.SubmissionID;
        let targetURL = process.env.REACT_APP_API_URL + "/api/submission/update/" + userID + "/" + pillarData.Submission.SubmissionID + "/" + pillarData.Team.TeamID;
        if (!submitSub) {
            targetURL += "/" + pillarData.Pillar.Pillar;
        }
        const data = `${encodeURIComponent("concerns")}=${encodeURIComponent(reviewerConcerns)}
                &${encodeURIComponent("accolades")}=${encodeURIComponent(reviewerAccolades)}`;

        getDataPost(targetURL, data).then(response => {
            const outcome: IOutcome = response;
            if (outcome.Success === true) {
                //update the concerns/accolades
                if (pillarData?.Pillar?.Comments !== undefined && pillarData?.Pillar?.Comments?.length > 0) {
                    pillarData.Pillar.Comments[0].Accolades = reviewerAccolades;
                    pillarData.Pillar.Comments[0].Concerns = reviewerConcerns;
                }
                if (submitSub) {
                    navigate("/dashboard");
                } else if (!saveOnly) {
                    let newPillar: string = "P1";
                    const currentPillar: string = pillarData.Pillar.Pillar;
                    if (currentPillar === Pillar.P1) {
                        newPillar = "P2";
                    } else if (currentPillar === Pillar.P2) {
                        newPillar = "P3";
                    } else if (currentPillar === Pillar.P3) {
                        newPillar = "P5";
                    } else if (currentPillar === Pillar.P5) {
                        newPillar = "ESSAY";
                    } else if (currentPillar === Pillar.ESSAY) {
                        newPillar = "COMMENTS";
                    }
                    //now move to the next pillar
                    navigate("/submission/" + userID + "/" + pillarData.Team.TeamID + "/" + pillarData.Submission.SubmissionID + "/" + newPillar);
                } else {
                    toggleSaved();
                }
            }
        })
    }

    /**
     * build the result
     *
     * @param responses
     */
    const buildResult = (responses: IResponse[]) => {
        if (responses !== null && responses.length > 0) {
            let result = responses.map(response => {
                return <Row
                    key={response.QuestionID + "-" + response.SubmissionID}>&#8210;&nbsp;{response.Response}</Row>
            })
            return <Container>{result}</Container>
        }
        return <div/>
    }

    /**
     * build the pillar data section
     */
    const buildPillarData = () => {

        //let header: string = "Responses";
        let results: ReactNode;
        if (pillarData?.Pillar?.Pillar === Pillar.GeneralComments) {
            //header = "Pillar Comments";
            results = pillarData?.Pillar?.Comments?.map(v => {
                if (v.Pillar !== Pillar.GeneralComments) {
                    return ReviewerComments(v);
                }
            })
        } else {
            results = pillarData?.Pillar?.Questions?.map(v => {
                if (v.QuestionType.includes("Header")) {
                    return <Row key={v.QuestionID + "-" + pillarData.Submission.SubmissionID}
                                className={"pillar-row pillar-header"}>{v.QuestionText} ({v.NumAnswered} out
                        of {v.NumQuestions} affirmative)</Row>
                } else if (v.Question.Responses !== null && v.Question.Responses.length > 0) {
                    //const result = parse(v.ApplicationData.Response.replace("\n", "<br>&#8210;&nbsp;"));
                    let result: string = "";
                    v.Question.Responses.map(response => {
                        result += response.Response + parse("<br>&#8210;&nbsp;");
                    })
                    if (pillarData?.Pillar.Pillar === Pillar.ESSAY) {
                        return <><Row key={v.QuestionKey}
                                      className={"pillar-row essay-row-question"}>{v.Question.QuestionTitle}</Row>
                            <Row key={v.QuestionKey + "-response"}
                                 className={"result"}>{buildResult(v.Question.Responses)}</Row></>
                    } else {
                        return <Row key={v.QuestionKey} className={"pillar-row"}><Col
                            className={"question"}>{v.Question.QuestionTitle}</Col><Col
                            className={"result"}>{buildResult(v.Question.Responses)}</Col></Row>
                    }
                }
                return <></>
            })
        }

        return <Container>
            {results}
        </Container>

    }

    const ReviewerComments = (comment: IReviewerComment) => {
        let concerns: string;
        let accolades: string;
        if (comment) {
            concerns = comment.Concerns;
            accolades = comment.Accolades;
        } else {
            concerns = "";
            accolades = "";
        }
        return <Container>
            <Row key={comment.Pillar + "-" + comment.SubmissionID}
                 className={"pillar-row pillar-header"}>{getPillar(comment.Pillar)}</Row>
            <Row key={"opportunities-" + comment.SubmissionID} className={"pillar-sub-header"}>Opportunities for for Improvement, Collaboration, and Networking</Row>
            <Row key={"concerns-" + comment.SubmissionID} className={"pillar-all-comments"}>{parse(concerns)}</Row>
            <Row key={"accolades-" + comment.SubmissionID} className={"pillar-sub-header"}>Strengths, Highlights, and Best Practices</Row>
            <Row key={"all-comments-" + comment.SubmissionID} className={"pillar-all-comments"}>{parse(accolades)}</Row>
        </Container>
    }

    /**
     * Get the piller header
     *
     * @param pillarNumber
     */
    function getPillar(pillarNumber?: string) {
        if (pillarNumber === "P2") {
            return "Pillar 2: Prevention & Early Detection";
        } else if (pillarNumber === "P3") {
            return "Pillar 3/4:  Advancing Treatment & Survivorship";
        } else if (pillarNumber === "P5") {
            return "Pillar 5:  Well-Being";
        } else if (pillarNumber === "ESSAY") {
            return "Essay";
        } else if (pillarNumber === "COMMENTS") {
            return "Comments";
        } else {
            return "Pillar 1: Health Education & Navigation";
        }
    }

    /**
     * build the jump start data
     */
    const jumpStartData = () => {
        if (pillarData.Pillar.Pillar !== Pillar.GeneralComments) {
            if (pillarData?.Team?.JumpStart !== undefined) {
                return <Row><Row className={"jumpstart-header"}><h3>Jumpstart</h3></Row>
                    <Row className={"jumpstart-section"}>
                        <span>{parse(pillarData?.Team?.JumpStart)}</span>
                    </Row></Row>
            }
            return <p>No data</p>
        } else {
            return <></>
        }
    }

    /**
     * check if the submission is ready to be submitted
     * TODO - find a better way
     *
     * @param comments
     */
    function checkSubmission(comments: IReviewerComment[]) {
        let foundP1: boolean = false;
        let foundP2: boolean = false;
        let foundP3: boolean = false;
        let foundP5: boolean = false;
        let foundEssay: boolean = false;
        comments.map(v => {
            if (v.Pillar === "P1") {
                foundP1 = true;
            }
            if (v.Pillar === "P2") {
                foundP2 = true;
            }
            if (v.Pillar === "P3") {
                foundP3 = true;
            }
            if (v.Pillar === "P5") {
                foundP5 = true;
            }
            if (v.Pillar === "ESSAY") {
                foundEssay = true;
            }
        })
        return foundP1 && foundP2 && foundP3 && foundP5 && foundEssay;
    }

    /**
     *  get the submit Button
     */
    const submitButton = () => {
        if (!pillarData.TeamSubmissionUserLinkage.Submitted) {
            let label: string = "Save Comments & Go To Next";
            const tmpComments = pillarData?.Pillar?.Comments ?? [];
            if (pillarData.Pillar.Pillar === Pillar.GeneralComments) {
                //check if user has viewed all the comments
                let found: boolean = checkSubmission(tmpComments)
                if (pillarData.TeamSubmissionUserLinkage.Submitted === false && found) {
                    return <Row>
                        <Button variant="primary" className="btn-submit" type="button" onClick={handleShow}>
                            Submit Review
                        </Button>
                    </Row>
                } else {
                    //return empty
                    return <></>
                }

            }
            return <Button variant="primary" className="btn-submit" type="submit">
                {label}
            </Button>
        }
        return <></>
    }

    const buildConfirmDialog = () => {
        if (pillarData.Pillar.Pillar === Pillar.GeneralComments) {
            return <ConfirmDialog title={"Submit Review"} body={<p>Are you sure that you are ready to submit?</p>}
                                  show={showConfirm} showHide={toggleConfirm} submitForm={handleSubmissionSubmit}/>
        } else {
            return <></>
        }
    }
    /**
     * build the submission data and form
     */
    const buildPillar = () => {
        return (
            <Container className={"form-container"} fluid>
                <InfoDialog title={"Thank you"} body={<p>Thank you for giving your time to The Gold Standard</p>}
                            show={showThankYou} showHide={toggleThankYou}/>
                <InfoDialog title={"Saved"} body={<p>Your comments have been saved!</p>} show={showSaved}
                            showHide={toggleSaved}/>
                <Row><h1>{getPillar(pillarData?.Pillar.Pillar)}</h1></Row>
                {jumpStartData()}
                <Row className={"comments-section"}>
                    <h3>Reviewer Comments</h3>
                    {buildPillarComments(pillarData?.Pillar)}
                    {buildConfirmDialog()}
                </Row>
                <Row>
                    <h3>Responses</h3>
                    {buildPillarData()}
                </Row>
            </Container>
        )
    }

    /**
     * build the pillar comments section
     *
     * @param pillar
     */
    const buildPillarComments = (pillar?: IPillarData) => {
        //you must check for active pillar before creating the CKEditor or you can't set the initial value
        /*        if (!disabled) {
                    if (pillarData.TeamSubmissionUserLinkage.Submitted) {
                        disabled = true;
                    }
                }*/
        let label: string = "Opportunities for for Improvement, Collaboration, and Networking";
        let keyConcerns = "p1-concerns";
        let keyAccolades = "p1-accolades";
        if (pillar?.Pillar === Pillar.P2) {
            keyConcerns = "p2-concerns";
            keyAccolades = "p2-accolades";
        } else if (pillar?.Pillar === Pillar.P3) {
            keyConcerns = "p3-concerns";
            keyAccolades = "p3-accolades";
        } else if (pillar?.Pillar === Pillar.P5) {
            keyConcerns = "p5-concerns";
            keyAccolades = "p5-accolades";
        } else if (pillar?.Pillar === Pillar.ESSAY) {
            keyConcerns = "essay-concerns";
            keyAccolades = "essay-accolades";
        } else if (pillar?.Pillar === Pillar.GeneralComments) {
            keyConcerns = "gen-comments";
            label = "General Feedback and Accolade Referrals";
        }
        if (pillar !== null && pillar !== undefined) {
            //let comments: string = "";
            let className: string = "col-left";

            /*
                        if (pillar?.Comments !== undefined && pillar?.Comments !== null && pillar?.Comments?.length > 0) {
                            comments = pillar?.Comments[0].Concerns;
                        }
            */
            if (pillarData.Pillar.Pillar === Pillar.GeneralComments) {
                className = "col-left no-show";
            }
            return <Form className="form-layout" onSubmit={handleSubmit} noValidate={true}>
                <Row className="mb-3">
                    <Form.Group as={Col} className={className}>
                        <Form.Label>Strengths, Highlights, and Best Practices</Form.Label>
                        <GSEditor onChange={setReviewerAccolades} editorLoaded={accoladesEditorLoaded}
                                  disabled={disabled} name={keyAccolades} value={reviewerAccolades}/>
                    </Form.Group>
                </Row>
                <Row className="mb-3">
                    <Form.Group as={Col} className={"col-left"}>
                        <Form.Label>{label}</Form.Label>
                        <GSEditor onChange={setReviewerConcerns} editorLoaded={concernsEditorLoaded} disabled={disabled}
                                  name={keyConcerns} value={reviewerConcerns}/>
                    </Form.Group>
                </Row>
                {disabled === false &&
                    <Row className={"submit-row"}>
                        <Col>
                            <Button variant="primary" onClick={handleSave} className="btn-submit" type="button">
                                Save Comments
                            </Button>
                        </Col>
                        <Col>
                            {submitButton()}
                        </Col>
                    </Row>
                }

            </Form>
        }
        return <div></div>
    };

    return (
        <Fragment>
            {buildPillar()}
        </Fragment>
    );
}